import * as React from "react";
import { Pin, PinOff } from "lucide-react";
import { useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { cn } from "../../lib/utils";
import { Loading } from "../../components/patterns/Loading";
import { Legend } from "../../components/ui/legend";
import { PanelComponent } from "../../components/ui/panelComponent";
import { ChartsFrame } from "../../components/ui/chartsFrame";
import { SciChart, } from "../../components/ui/sci-chart";
import { useChartGroupContext } from "./useChartGroupContext";
import { TimeRangeButtons } from "./TimeRangeButtons";
import { LegendEntryContainer } from "./LegendEntryContainer";
import { DetailedSeriesLegendEntryContainer } from "./DetailedSeriesLegendEntryContainer";
import { applyMergePanels } from "./panels";
const PanelLoading = ({ panel, switches }) => {
    return (React.createElement(PanelComponent, { chartHeight: panel.height, chartName: panel.name, switches: switches, buttons: panel.buttons, isCollapsed: true, hideCollapse: true },
        React.createElement(Loading, null)));
};
function useTimeSeriesData(panel, span, category, site) {
    const data = panel.timeSeriesData;
    if (typeof data === "function") {
        // this is intentional and should not violate the rule
        // of hooks here; the typeof data should remain the same
        // for one component / time series panel
        const client = useQueryClient();
        const [range, setRange] = React.useState(span);
        const meta = React.useMemo(() => {
            const [from, to] = range;
            return {
                charts: true,
                category,
                site,
                type: "panel",
                start: from,
                end: to,
            };
        }, [category, site, range]);
        const result = useSuspenseQuery({
            queryKey: ["chart.panels", panel.key, panel.mergePanelIds, range],
            staleTime: 300000,
            queryFn: () => data(range),
            meta,
        });
        React.useEffect(() => {
            const [currentStart, currentEnd] = span;
            const [previousStart, previousEnd] = range;
            if (currentStart < previousStart || currentEnd > previousEnd) {
                let active = true;
                client
                    .fetchQuery({
                    queryKey: ["chart.panels", panel.key, panel.mergePanelIds, span],
                    queryFn: () => data(span),
                    meta,
                })
                    .then(() => active && setRange(span));
                return () => {
                    active = false;
                };
            }
        }, span);
        return result.data;
    }
    return data;
}
function hasDetailLegend(series) {
    const l = series.legendEntry;
    if (!l) {
        return false;
    }
    return "isDetailed" in l && l.isDetailed;
}
function isWithin(legend, fullRange) {
    if ("visibleRange" in legend && Array.isArray(legend.visibleRange)) {
        const [start, end] = legend.visibleRange;
        const [fullStart, fullEnd] = fullRange;
        return end * 1000 > fullStart.valueOf() && start * 1000 < fullEnd.valueOf();
    }
    return true;
}
/**
 * Merging turns static timeSeriesData into a loader; a new key remounts the item,
 * so useTimeSeriesData never switches its hook branch within one instance.
 */
function getChartItemKey(panel) {
    var _a;
    return ((_a = panel.mergePanelIds) === null || _a === void 0 ? void 0 : _a.length)
        ? `${panel.key}:${panel.mergePanelIds.join(",")}`
        : panel.key;
}
const PanelContainer = ({ name, panel, last, switches, }) => {
    var _a, _b, _c, _d;
    const [invisible, setInvisible] = React.useState([]);
    const { axisSynchronizer, modifierGroup, verticalGroup, settings, licenseKey, timeMarkers, visibleRange, dataRange, currentTime, category, site, } = useChartGroupContext();
    const timeSeriesData = useTimeSeriesData(panel, dataRange, category, site);
    const [isCollapsed, setIsCollapsed] = React.useState(true); // "timeSeries" only
    const toggleHeight = React.useCallback(() => setIsCollapsed((val) => !val), []);
    const detailedSeriesItems = (_b = (_a = timeSeriesData === null || timeSeriesData === void 0 ? void 0 : timeSeriesData.series) === null || _a === void 0 ? void 0 : _a.filter(hasDetailLegend)) !== null && _b !== void 0 ? _b : [];
    const legendEntries = (_c = timeSeriesData === null || timeSeriesData === void 0 ? void 0 : timeSeriesData.series) === null || _c === void 0 ? void 0 : _c.filter((series) => series.legendEntry &&
        !detailedSeriesItems.includes(series) &&
        isWithin(series.legendEntry, visibleRange)).map((series) => {
        const seriesKey = series.key;
        const visible = !invisible.includes(seriesKey);
        const legend = series.legendEntry;
        const onToggle = () => {
            setInvisible(visible
                ? [...invisible, seriesKey]
                : invisible.filter((m) => m !== seriesKey));
        };
        series.hidden = !visible;
        if (!isCollapsed || !legend.isAdditional) {
            return (React.createElement(LegendEntryContainer, { key: seriesKey, time: currentTime, chartDivId: panel.key, entry: legend, series: series, visible: visible, onToggle: onToggle }));
        }
        return null;
    });
    const groupedDetailedItems = new Map();
    if (legendEntries && detailedSeriesItems.length > 0) {
        for (const item of detailedSeriesItems) {
            const legend = item.legendEntry;
            const key = legend.name;
            groupedDetailedItems.set(key, [
                ...((_d = groupedDetailedItems.get(key)) !== null && _d !== void 0 ? _d : []),
                item,
            ]);
        }
        for (const [key, items] of groupedDetailedItems) {
            legendEntries.push(React.createElement(DetailedSeriesLegendEntryContainer, { key: key, time: currentTime, chartDivId: panel.key, detailedItems: items }));
        }
    }
    const height = isCollapsed
        ? panel.height
        : panel.extendedHeight || panel.height;
    const noExtend = panel.hideCollapse || !panel.extendedHeight;
    const chart = (React.createElement(SciChart, { currentTime: currentTime, timeSeriesData: Object.assign({ chartDivId: panel.key }, timeSeriesData), staticYAxis: panel.staticYAxis, gridlines: panel.gridlines, axisSynchronizer: axisSynchronizer, verticalGroup: verticalGroup, modifierGroup: modifierGroup, showYCrossHairLine: panel.type === "timeSeries", padding: panel.padding, removeNegativeLabels: panel.removeNegativeLabels, removeYAxis: panel.removeYAxis, showXAxis: panel.showXAxis, licenseKey: licenseKey, control: settings.controlType, useWheel: settings.useWheel, magnifyAxis: settings.magnifyAxis, isTimeDisplay: panel.isTimeDisplay, onSurfaceCreated: panel.onSurfaceCreated, timeMarkers: timeMarkers }));
    return (React.createElement(PanelComponent, { chartName: name, chartHeight: height, legend: !panel.hideLegend && React.createElement(Legend, { legendEntries: legendEntries }), switches: switches, noBorder: panel.type === "gantt", buttons: panel.buttons, isLast: last, isCollapsed: isCollapsed, hideCollapse: noExtend, onToggleHeight: toggleHeight }, panel.isTimeDisplay ? (React.createElement("div", { className: "h-full relative" },
        chart,
        React.createElement(TimeRangeButtons, null))) : (chart)));
};
function usePinned(panel) {
    const key = `portal:charts/pin:${panel.key}`;
    const [pinned, setPinned] = React.useState(() => {
        if (panel.isTimeDisplay) {
            return true;
        }
        return !!localStorage.getItem(key);
    });
    const toggle = () => {
        if (!panel.isTimeDisplay) {
            setPinned(!pinned);
            if (pinned) {
                localStorage.removeItem(key);
            }
            else {
                localStorage.setItem(key, "yes");
            }
        }
    };
    return [pinned, toggle];
}
const ChartItem = ({ panel, openSwitches, isOpen, onChange, color, last, hideLegend, }) => {
    const [pinned, togglePinned] = usePinned(panel);
    const panelSwitches = Object.values(openSwitches)
        .filter((s) => s.relatedPanelKey === panel.key)
        .map((s) => {
        return {
            key: s.key,
            name: s.key,
            icon: s.icon,
            isOpen: s.isOpen,
            onToggleOpen: () => {
                onChange(Object.fromEntries(s.registeredPanels.map((k) => [k, !isOpen(k)])));
            },
        };
    });
    const PinIcon = pinned ? Pin : PinOff;
    return (React.createElement(ChartsFrame, { color: panel.color || color, key: panel.key, hideLegend: hideLegend, pinned: pinned }, isOpen(panel.key) && (React.createElement(React.Suspense, { fallback: React.createElement(PanelLoading, { panel: panel, switches: panelSwitches }) }, panel.type === "noData" ? (React.createElement(PanelComponent, { chartHeight: panel.height, chartName: panel.name, switches: panelSwitches, buttons: panel.buttons, isCollapsed: true, hideCollapse: true },
        React.createElement("div", { id: panel.key, className: "h-full" }))) : (React.createElement(PanelContainer, { name: panel.name && (React.createElement("div", { className: "flex flex-row items-center gap-2" },
            !panel.hidePin && (React.createElement(PinIcon, { className: cn("h-3.5 w-3.5 cursor-pointer hover:text-skvelightblue-600", !pinned && "text-muted-foreground"), onClick: togglePinned })),
            React.createElement("span", null, panel.name))), panel: panel, last: last, switches: panelSwitches }))))));
};
export const ChartsFrameContainer = ({ panels: allPanels, color, }) => {
    const { settings, mergePanels } = useChartGroupContext();
    const panels = React.useMemo(() => applyMergePanels(allPanels !== null && allPanels !== void 0 ? allPanels : [], Object.values(mergePanels)), [allPanels, mergePanels]);
    const defaultOpenPanels = panels === null || panels === void 0 ? void 0 : panels.filter((panel) => !panel.relatedOpenSwitch || panel.relatedOpenSwitch.isOpen).map((panel) => panel.key);
    const [openPanels, setOpenPanels] = React.useState({});
    const openSwitches = {};
    const isOpen = (key) => {
        var _a;
        return (_a = openPanels[key]) !== null && _a !== void 0 ? _a : defaultOpenPanels.includes(key);
    };
    panels === null || panels === void 0 ? void 0 : panels.forEach((panel) => {
        if (panel.relatedOpenSwitch) {
            const existingSwitch = Object.values(openSwitches).find((s) => { var _a; return s.key === ((_a = panel.relatedOpenSwitch) === null || _a === void 0 ? void 0 : _a.key); });
            if (existingSwitch) {
                const existingKey = Object.keys(openSwitches).find((key) => openSwitches[key] === existingSwitch);
                openSwitches[existingKey] = Object.assign(Object.assign({}, existingSwitch), { registeredPanels: existingSwitch.registeredPanels.includes(panel.key)
                        ? [...existingSwitch.registeredPanels]
                        : [...existingSwitch.registeredPanels, panel.key] });
            }
            else {
                openSwitches[panel.relatedOpenSwitch.key] = Object.assign(Object.assign({}, panel.relatedOpenSwitch), { registeredPanels: [panel.key] });
            }
        }
    });
    return (React.createElement(React.Fragment, null, panels === null || panels === void 0 ? void 0 : panels.map((panel, i) => (React.createElement(ChartItem, { panel: panel, key: getChartItemKey(panel), color: color, hideLegend: settings.hideLegend, isOpen: isOpen, openSwitches: openSwitches, last: i + 1 === panels.length, onChange: (updated) => setOpenPanels((current) => (Object.assign(Object.assign({}, current), updated))) })))));
};
//# sourceMappingURL=ChartsFrameContainer.js.map